import axios from 'axios';

// Determine API base URL - works for both local development and production
const getApiBaseUrl = () => {
  // For local development, use the backend port at root API level
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/v1';
  }
  
  // For production, nginx adds /api/ prefix, so we need /v1 only
  return '/api/v1';  
};

// Determine health domain base URL for health-specific endpoints
const getHealthApiBaseUrl = () => {
  // For local development, use the backend port with health domain
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/v1';
  }
  
  // For production, nginx adds /api/ prefix, so we need /v1 only
  return '/api/v1';  
};

const API_BASE_URL = getApiBaseUrl();
const HEALTH_API_BASE_URL = getHealthApiBaseUrl();

// Export the base URL functions for use in other components
export { getApiBaseUrl, getHealthApiBaseUrl };

// Main API instance for auth and general endpoints
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Health API instance for health-specific endpoints
const healthApi = axios.create({
  baseURL: HEALTH_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// For compatibility with legal functions (temp fix)
const legalApi = healthApi;

// Legal Policy Management Endpoints
export const uploadLegalPolicy = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await legalApi.post('/policies/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const searchLegalPolicies = async (query = '', category = '', source = '', limit = 20, offset = 0) => {
  const response = await legalApi.get('/policies/search', {
    params: { query, category, source, limit, offset },
  });
  return response.data;
};

export const getLegalPolicySources = async () => {
  const response = await legalApi.get('/policies/sources');
  return response.data;
};

export const deleteLegalPolicy = async (policyId) => {
  const response = await legalApi.delete(`/policies/${policyId}`);
  return response.data;
};

export const deleteLegalPoliciesBulk = async (policyIds) => {
  const response = await legalApi.delete('/policies/bulk', { data: policyIds });
  return response.data;
};

export const deleteLegalPoliciesBySource = async (sourceFile) => {
  const response = await legalApi.delete(`/policies/source/${sourceFile}`);
  return response.data;
};

// Legal Discovery Processing
export const processLegalDiscovery = async (text, useRules = true) => {
  const response = await legalApi.post('/discovery/process', { 
    text, 
    use_rules: useRules 
  });
  return response.data;
};

// Health Document Classification - use healthApi for health domain
export const classifyDocument = async (text, useHealthPolicies = true) => {
  const endpoint = '/health/documents/classify';
  const response = await api.post(endpoint, { text, use_health_policies: useHealthPolicies });
  return response.data;
};

// Enhanced Health Document Classification with PHI detection
export const classifyDocumentEnhanced = async (text, options = {}) => {
  const {
    useHealthPolicies = true,
    useHyde = true,
    useSemanticRetrieval = true
  } = options;
  
  const response = await api.post('/health/documents/classify', { 
    text, 
    use_health_policies: useHealthPolicies,
    use_hyde: useHyde,
    use_semantic_retrieval: useSemanticRetrieval
  });
  return response.data;
};

// Paragraph-level Classification - use legalApi for legal domain
export const classifyParagraphs = async (text, options = {}) => {
  const {
    chunkSize = 1000,
    chunkOverlap = 100,
    useScgRules = false
  } = options;
  
  const response = await legalApi.post('/classify/paragraphs', {
    text,
    chunk_size: chunkSize,
    chunk_overlap: chunkOverlap,
    use_scg_rules: useScgRules
  });
  return response.data;
};

// Document Upload
export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Upload and Classify Document with Portion Marks
export const uploadAndClassifyParagraphs = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500,
    useLlmSegmentation = true
  } = options;
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  formData.append('use_llm_segmentation', useLlmSegmentation.toString());
  
  const response = await api.post('/upload/classify-paragraphs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Upload and generate formatted document with portion marks
export const uploadAndClassifyFormatted = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500
  } = options;
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  
  const response = await api.post('/upload/classify-formatted', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// Format document using existing classification results (no re-classification)
export const formatClassifiedDocument = async (file, classificationData) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('classification_data', JSON.stringify(classificationData));
  
  const response = await api.post('/format-document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// SCG Management
export const uploadSCGDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/scg/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getSCGRules = async () => {
  const response = await api.get('/scg/rules');
  return response.data;
};

// SCG Rule Management
export const searchSCGRules = async (query = "", classificationLevel = "", source = "", limit = 50, offset = 0) => {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  if (classificationLevel) params.append('classification_level', classificationLevel);
  if (source) params.append('source', source);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  const response = await api.get(`/scg/search?${params.toString()}`);
  return response.data;
};

export const getSCGSources = async () => {
  const response = await api.get('/scg/sources');
  return response.data;
};

export const getSCGRule = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}`);
  return response.data;
};

export const updateSCGRule = async (ruleId, updateData) => {
  const response = await api.put(`/scg/rule/${ruleId}`, updateData);
  return response.data;
};

export const deleteSCGRule = async (ruleId) => {
  const response = await api.delete(`/scg/rule/${ruleId}`);
  return response.data;
};

export const deleteSCGRulesBulk = async (ruleIds) => {
  const response = await api.post('/scg/delete/bulk', { rule_ids: ruleIds });
  return response.data;
};

export const deleteSCGRulesBySource = async (sourceFile) => {
  const response = await api.delete(`/scg/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// SCG Source Information
export const getSCGRuleSourceInfo = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}/source`);
  return response.data;
};

export const getSCGPdfUrl = (fileHash, page = null) => {
  // For Docker/production, use relative URL to avoid localhost port issues
  // For development, use the full localhost:8000 URL
  let baseUrl;
  if (process.env.NODE_ENV === 'development') {
    baseUrl = `${API_BASE_URL}/scg/pdf/${fileHash}`;
  } else {
    // Use relative URL for Docker/production to work with nginx proxy
    baseUrl = `/api/v1/scg/pdf/${fileHash}`;
  }
  return page ? `${baseUrl}?page=${page}` : baseUrl;
};

// Document Search
export const searchDocuments = async (query, topK = 10, filters = null, rewriteQuery = false) => {
  const response = await api.post('/search', {
    query,
    top_k: topK,
    filters,
    rewrite_query: rewriteQuery
  });
  return response.data;
};

// List Documents
export const listDocuments = async (limit = 50) => {
  const response = await api.get(`/documents?limit=${limit}`);
  return response.data;
};

// Generate Embedding
export const generateEmbedding = async (text) => {
  const response = await api.post('/embed', { text });
  return response.data;
};

// Query Rewriting
export const rewriteQuery = async (query) => {
  const response = await api.post(`/query/rewrite?query=${encodeURIComponent(query)}`);
  return response.data;
};

// Health Check
export const healthCheck = async () => {
  const response = await axios.get('/health'); // Use direct call, not through api instance
  return response.data;
};

// Tearline Document Sanitization
export const sanitizeDocument = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, preClassifiedDocument = null) => {
  const response = await api.post('/tearline/sanitize', {
    text,
    target_classification: targetClassification,
    target_caveats: targetCaveats,
    target_releasability: targetReleasability,
    max_iterations: maxIterations,
    sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
    pre_classified_document: preClassifiedDocument
  });
  return response.data;
};

// Streaming Tearline Document Sanitization
export const sanitizeDocumentStream = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, onProgress, preClassifiedDocument = null) => {
  console.log('API: Starting streaming sanitization request...');
  console.log('API: onProgress callback provided:', typeof onProgress);
  console.log('API: Pre-classified document provided:', preClassifiedDocument ? 'Yes' : 'No');
  
  return new Promise((resolve, reject) => {
    // First, start the sanitization process
    fetch(`${API_BASE_URL}/tearline/sanitize/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
        pre_classified_document: preClassifiedDocument
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log('API: Got streaming response, starting to read...');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;

      function readStream() {
        return reader.read().then(({ done, value }) => {
          if (done) {
            console.log('API: Stream finished reading');
            resolve(finalResult);
            return;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('API: Received streaming update:', data);
                
                // Call progress callback with update
                if (onProgress) {
                  console.log('API: Calling onProgress callback with:', data);
                  setTimeout(() => {
                    try {
                      onProgress(data);
                      console.log('API: onProgress callback completed successfully');
                    } catch (error) {
                      console.error('API: Error in onProgress callback:', error);
                    }
                  }, 0); // Use setTimeout to ensure async execution
                } else {
                  console.log('API: No onProgress callback available');
                }
                
                // Store final result
                if (data.final_result) {
                  console.log('API: Setting final result:', data.final_result);
                  finalResult = data.final_result;
                }
              } catch (e) {
                console.warn('API: Failed to parse streaming data:', line);
              }
            }
          }
          
          // Continue reading
          return readStream();
        });
      }
      
      return readStream();
    })
    .catch(error => {
      console.error('API: Streaming request failed:', error);
      reject(error);
    });
  });
};

// Testing and Optimization APIs
export const startTestRun = async (testType = 'classification', testQueries = null, configParams = null) => {
  const response = await api.post('/testing/run', {
    test_type: testType,
    test_queries: testQueries,
    config_params: configParams
  });
  return response.data;
};

export const getTestRunStatus = async (testId) => {
  const response = await api.get(`/testing/run/${testId}`);
  return response.data;
};

export const startConfigOptimization = async (optimizationParams, testQueries = null, scoringWeights = null) => {
  const response = await api.post('/testing/optimize', {
    test_queries: testQueries,
    optimization_params: optimizationParams,
    scoring_weights: scoringWeights
  });
  return response.data;
};

export const getOptimizationStatus = async (optimizationId) => {
  const response = await api.get(`/testing/optimize/${optimizationId}`);
  return response.data;
};

export const getDefaultTestQueries = async () => {
  const response = await api.get('/testing/default-queries');
  return response.data;
};

export const getConfigOptimizationTemplates = async () => {
  const response = await api.get('/testing/config-templates');
  return response.data;
};

// ==================== FOIA PROCESSING APIs ====================

// FOIA Document Processing
export const processFOIADocument = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// FOIA File Processing
export const processFOIAFile = async (file, agency = '', requesterInterest = '', useHyde = true) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  formData.append('use_hyde', useHyde);
  
  const response = await api.post('/foia/process/file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// FOIA Rule Management
export const uploadFOIAGuidance = async (file, agency = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  
  const response = await api.post('/foia/rules/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const searchFOIARules = async (query = '', exemptionCategory = '', source = '', limit = 50, offset = 0) => {
  const params = new URLSearchParams({
    query,
    exemption_category: exemptionCategory,
    source,
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  const response = await api.post('/foia/rules/search', {
    query,
    exemption_category: exemptionCategory,
    source,
    limit,
    offset
  });
  return response.data;
};

export const getFOIASources = async () => {
  const response = await api.get('/foia/rules/sources');
  return response.data;
};

export const getFOIAExemptions = async () => {
  const response = await api.get('/foia/rules/exemptions');
  return response.data;
};

export const getFOIARule = async (ruleId) => {
  const response = await api.get(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARule = async (ruleId) => {
  const response = await api.delete(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARulesBulk = async (ruleIds) => {
  const response = await api.post('/foia/rules/delete/bulk', {
    rule_ids: ruleIds
  });
  return response.data;
};

export const deleteFOIARulesBySource = async (sourceFile) => {
  const response = await api.delete(`/foia/rules/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// Enhanced FOIA Processing (Standard Exemptions + Rule-based + Conflict Detection)
export const processEnhancedFOIA = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process/enhanced', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// Enhanced FOIA Processing with Streaming
export const processEnhancedFOIAStream = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process/enhanced/stream', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// PDF FOIA Redaction - Returns visually redacted PDF with full analysis
export const redactPDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/redact/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  // Convert base64 PDF data to blob for download
  const pdfData = response.data.pdfData;
  const binaryString = atob(pdfData);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
  
  return {
    pdfBlob: pdfBlob,
    filename: response.data.filename,
    redactionCount: response.data.redactionCount,
    exemptionsUsed: response.data.exemptionsUsed,
    processingTime: response.data.processingTime,
    confidenceScore: response.data.confidenceScore,
    mappingStats: response.data.mappingStats,
    textAnalysis: response.data.textAnalysis  // Full analysis data
  };
};

// PDF FOIA Analysis - Returns detailed analysis only
export const analyzePDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/analyze/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// Model Management
export const getAvailableModels = async () => {
  const response = await api.get('/config/models/available');
  return response.data;
};

export const getCurrentModel = async () => {
  const response = await api.get('/config/models/current');
  return response.data;
};

export const validateModelConfig = async (provider, modelId) => {
  const response = await api.post('/config/models/validate', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const switchModel = async (provider, modelId) => {
  const response = await api.post('/config/models/switch', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const clearModelCache = async () => {
  const response = await api.post('/config/models/cache/clear');
  return response.data;
};

// Continue Tearline Sanitization Stream  
export const continueSanitizeDocumentStream = async (targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, previousResult, onProgress) => {
  console.log('API: Starting continue streaming sanitization request...');
  console.log('API: onProgress callback provided:', typeof onProgress);
  
  return new Promise((resolve, reject) => {
    // Start the continue sanitization process
    fetch(`${API_BASE_URL}/tearline/continue/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        previous_result: previousResult
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            console.log('API: Continue stream ended');
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          lines.forEach(line => {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('API: Continue stream data:', data);

                if (onProgress) {
                  onProgress(data);
                }

                if (data.status === 'completed') {
                  console.log('API: Continue sanitization completed successfully');
                  resolve(data.final_result);
                } else if (data.status === 'error') {
                  console.error('API: Continue sanitization error:', data.error);
                  reject(new Error(data.message || data.error || 'Continue sanitization failed'));
                }
              } catch (parseError) {
                console.error('API: Failed to parse continue stream data:', parseError, line);
              }
            }
          });

          readStream();
        }).catch(error => {
          console.error('API: Continue stream read error:', error);
          reject(error);
        });
      };

      readStream();
    })
    .catch(error => {
      console.error('API: Continue fetch error:', error);
      reject(error);
    });
  });
};

// Process PDF for FOIA with initial redactions
export const processPDFForFOIA = async (pdfFile, agency = "", requesterInterest = "") => {
  const formData = new FormData();
  formData.append('file', pdfFile);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);

  const response = await api.post('/foia/process-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  return response.data;
};

// Generate redacted PDF with modified redactions
export const generateRedactedPDF = async (options) => {
  const {
    pdf_file,
    redactions = [],
    agency = "",
    requester_interest = ""
  } = options;

  const formData = new FormData();
  if (pdf_file) {
    formData.append('file', pdf_file);
  }
  formData.append('redactions', JSON.stringify(redactions));
  formData.append('agency', agency);
  formData.append('requester_interest', requester_interest);

  const response = await api.post('/foia/generate-redacted-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob' // Expecting PDF binary data
  });
  
  return response;
};

// Set up axios interceptors for both API instances
const setupInterceptors = (apiInstance) => {
  // Add a request interceptor to include the auth token
  apiInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor to handle auth errors
  apiInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('authToken');
        // Redirect to login page
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Healthcare Document Classification Functions
export const classifyHealthDocument = async (text, options = {}) => {
  const response = await healthApi.post('/health/documents/classify', {
    text,
    ...options
  });
  return response.data;
};

export const classifyHealthDocumentStream = (text, options = {}) => {
  // Return an object with streaming capabilities (placeholder implementation)
  return {
    onData: (callback) => {
      // For now, just do a regular API call and simulate streaming
      classifyHealthDocument(text, options)
        .then(result => {
          callback({ status: 'completed', final_result: result });
        })
        .catch(error => {
          console.error('Health document classification error:', error);
        });
    },
    onError: (callback) => {
      // Error handling placeholder
    }
  };
};

// Setup interceptors for both API instances
setupInterceptors(api);
setupInterceptors(legalApi);

// Export the main API instance as default (for auth endpoints)
export default api;